<script setup lang="ts">
import { computed, ref, onMounted, watch, nextTick, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import { useSignet } from '@/composables/useSignet'


type StepDef = {
  comp: any
  titleKey: string
  when?: (ctx: { umsokn: any }) => boolean
  props?: Record<string, any>
}

const props = defineProps<{
  appId: number
  updateKey: string
  steps: StepDef[]
}>()

const route = useRoute()
const router = useRouter()

const umoknType = provide("umsoknType", props.updateKey)
const { umsokn, createUmsokn, updateUmsokn } = useUmsokn()
const { signetDocument, spinner, signInContract } = useSignet()

const isLoading = ref(false)
const isUpdating = ref(false)
const stepRef = ref<any>()

const showDialog = ref(false)
const dialogTitle = ref('Tilkynning')
const dialogMessage = ref('Óvænt villa kom upp á')

const headerRef = ref<HTMLElement | null>(null)

const currentStep = computed(() => Number(route.query.step ?? 0))

// Logic for conditional steps
const visibleSteps = computed<StepDef[]>(() =>
  props.steps.filter(s => {
    if (typeof s.when === 'function') {
      try { return s.when({ umsokn: umsokn.value }) } catch { return false }
    }
    return true
  })
)
const lastVisibleIndex = computed(() => Math.max(0, visibleSteps.value.length - 1))


onMounted(async () => {
  if (currentStep.value > 0 && !umsokn.value) {
    try {
      isLoading.value = true
      await createUmsokn(props.appId) // acts as fetch-or-create
    } catch (err) {
      console.error('Failed to fetch/create umsokn:', err)
      dialogTitle.value = 'Villa'
      dialogMessage.value = 'Ekki tókst að sækja umsóknina'
      showDialog.value = true
      goToStep(0)
    } finally {
      isLoading.value = false
    }
  }

  await nextTick()
  requestAnimationFrame(() => {
    const el = headerRef.value?.querySelector('.v-stepper-item--selected') as HTMLElement | null
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  })
})

watch(currentStep, async () => {
  await nextTick()
  const el = headerRef.value?.querySelector('.v-stepper-item--selected') as HTMLElement | null
  el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
})

function showError(title: string, message: string) {
  dialogTitle.value = title
  dialogMessage.value = message
  showDialog.value = true
}

function goToStep(step: number) {
  // Clamp to visible range (guards against stale route index when visibility changes)
  const clamped = Math.max(0, Math.min(step, lastVisibleIndex.value))
  router.push({ query: { step: clamped } })
}

function canNavigateToStep(stepIndex: number): boolean {
  // Can navigate to current step or any step that has been completed
  return stepIndex <= Math.max(currentStep.value, umsokn.value?.currentStep ?? 0)
}

async function nextStep() {
  if (isLoading.value || isUpdating.value) return // guard against double-click

  isLoading.value = true
  try {
    if (stepRef.value?.validate) {
      const isValid = await stepRef.value.validate()
      if (!isValid) {
        isLoading.value = false
        return
      }
    }

    const stepToSave = currentStep.value
    const nextStepValue = currentStep.value + 1
    
    // Update the highest step reached
    if (umsokn.value) {
      umsokn.value.currentStep = Math.max(umsokn.value.currentStep ?? 0, nextStepValue)
    }
    
    // Move to next step immediately
    goToStep(nextStepValue)
    isLoading.value = false

    // Update in the background
    if (stepToSave > 0) {
      isUpdating.value = true
      try {
        if (stepToSave === lastVisibleIndex.value) {
          await createUmsokn(props.appId, true)
        } else {
          await updateUmsokn(props.updateKey)
        }
      } catch (e) {
        showError('Villa', 'Óvænt villa kom upp á')
        console.error('Background update failed', e)
        // Go back to the step where error occurred
        goToStep(stepToSave)
      } finally {
        isUpdating.value = false
      }
    }
  } catch (e) {
    showError('Villa', 'Óvænt villa kom upp á')
    console.error('nextStep failed', e)
    isLoading.value = false
  }
}

function prevStep() {
  if (currentStep.value > 0) goToStep(currentStep.value - 1)
}



const stepComponent = computed(() => visibleSteps.value[currentStep.value]?.comp ?? null)

const propsForStep = computed(() => {
  const base = {
    umsokn: umsokn.value,
    setLoading: (val: boolean) => (isLoading.value = val),
    ...(visibleSteps.value[currentStep.value]?.props ?? {}) // pass per-step props (e.g., runBankCheck)
  }

  // Summary step
  if (currentStep.value === lastVisibleIndex.value - 1) {
    return {
      ...base,
      signetDocument: signetDocument.value,
      documentLoading: spinner.value,
      handlePrev: prevStep
    }
  }

  // Undirritun step
  if (currentStep.value === lastVisibleIndex.value) {
    return {
      ...base,
      signetDocument: signetDocument.value,
      documentLoading: spinner.value,           
      setLoading: (val: boolean) => (isLoading.value = val),
      handlePrev: prevStep,
      disable: false,
      showError,
      projectId: umsokn.value?.id,
      appId: props.appId,         
      lang: 0                    
    }
  }

  return base
})
</script>

<template>
  <div class="form-wrapper" style="min-height: 250px;">
    <div v-if="isLoading" class="loading-blur-overlay">
      <v-progress-circular indeterminate size="64" color="primary" class="blur-spinner" />
    </div>

    <!-- Stepper only shown after Begin step -->
    <v-stepper v-if="currentStep > 0" :model-value="currentStep">
      <div ref="headerRef" class="stepper-header">
        <v-stepper-header>
          <v-stepper-item
            v-for="(step, index) in visibleSteps.slice(1)"
            :key="index + 1"
            :value="index + 1"
            :complete="index + 1 < (umsokn?.currentStep ?? 0)"
            :editable="false"
          >
            <div 
              @click="canNavigateToStep(index + 1) && index + 1 !== currentStep && goToStep(index + 1)"
              :style="{ cursor: canNavigateToStep(index + 1) && index + 1 !== currentStep ? 'pointer' : 'default' }"
            >
              {{ $t(step.titleKey) }}
            </div>
          </v-stepper-item>
        </v-stepper-header>
      </div>
    </v-stepper>

    <!-- Current step content -->
    <component v-if="stepComponent" :is="stepComponent" ref="stepRef" v-bind="propsForStep" />

    <div
      class="forms__button__container"
      v-if="currentStep > 0 && currentStep < lastVisibleIndex"
    >
      <button class="forms__button forms__button__back" @click="prevStep">
        {{ $t('lifeyri_umsokn_tilbaka') }}
      </button>
      <button
        class="forms__button forms__button__forward"
        @click="nextStep"
        :disabled="isLoading || isUpdating"
      >
        <v-progress-circular
          v-if="isLoading || isUpdating"
          indeterminate
          size="18"
          width="2"
          color="white"
          class="mr-2"
        />
        {{ $t('lifeyri_umsokn_afram') }}
      </button>
    </div>

    <!-- Shared error dialog -->
    <v-dialog v-model="showDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">
          {{ dialogTitle }}
        </v-card-title>
        <v-card-text>{{ dialogMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showDialog = false">Ok</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">
@use "@/scss/settings/mixins" as mixins;
@use "@/scss/settings/variables" as *;
.v-stepper {
  margin-bottom: 40px;
}
// Dark mode support for scrollbar using global mixin
.dark-mode .v-stepper-header {
  scrollbar-color: $color-text-muted $color-card-dark;
  &::-webkit-scrollbar {
    background: $color-card-dark;
  }
  &::-webkit-scrollbar-thumb {
    background: $color-border-dark;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: $color-primary-dark-muted;
  }
}





.v-stepper-header {
  position: relative;
  display: flex;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #bdbdbd #f5f5f5;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    height: 6px;
    background: #f5f5f5;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #bdbdbd;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a0a0a0;
  }
  &::-webkit-scrollbar-corner {
    background: transparent;
  }

  mask-image: linear-gradient(
    to right,
    transparent 0,
    black 30px,
    black calc(100% - 30px),
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0,
    black 30px,
    black calc(100% - 30px),
    transparent 100%
  );
}



.forms__button__container {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
}

.forms__button {
  font-size: 16px;
  font-weight: bold;
  padding: 12px 24px;
  border: none;
  border-radius: 5px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.forms__button__back {
  background-color: #cccccc;
  color: #333;
}

.forms__button__forward {
  background: linear-gradient(180deg, #00bcb3 0%, #00877c 100%);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.form-wrapper { position: relative; }

.loading-blur-overlay {
  position: absolute;
  inset: 0;
  backdrop-filter: blur(1.5px);
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

.blur-spinner { pointer-events: none; }
</style>
